import React, { useEffect, useState } from "react";
import { ConfigProvider, DatePicker, Select, Spin } from "antd";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { IoMdArrowDropdown } from "react-icons/io";
import * as Yup from "yup";
import dayjs from "dayjs";
import {
  SubmitCredentialData,
  EditSensCredential,
} from "../../../Api/SuperAdminVault/Credentials";
import { encryptVaultSecret } from "../../Common/Encrypt-Decrypt";
import CredentialsOtpModal from "./CredentialsOtpModal";

const CredentialForm = ({
  credentialId,
  mode = "add",
  setModalIsOpen,
  onSuccess,
}) => {
  const [initialValues, setInitialValues] = useState({
    credential_type: "",
    credential_name: "",
    provider_name: "",
    username: "",
    secret: "",
    expires_at: "",
    is_sensitive: false,
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const isView = mode === "view";
  const isEdit = mode === "edit";

  const validationSchema = Yup.object().shape({
    credential_type: Yup.string().required("Type is required"),
    credential_name: Yup.string()
      .required("Name is required")
      .min(3, "Min 3 characters required"),
    provider_name: Yup.string().required("Owner is required"),
    username: Yup.string().required("Username / Identifier is required"),
    secret: Yup.string().required("Secret is required"),
    expires_at: Yup.string().required("Expires date is required"),
    notes: Yup.string().max(250, "Max 250 characters only"),
  });

  const typeOptions = [
    {
      value: "",
      label: (
        <div className="text-[1vw] px-[0.2vw] pb-[0.1vw] text-[#a9a9a9] font-normal">
          Select Type
        </div>
      ),
      disabled: true,
      search: "Select Type",
    },
    {
      value: "api_key",
      label: (
        <div className="text-[1vw] px-[0.2vw] pb-[0.1vw] font-normal">
          API Key
        </div>
      ),
      search: "API Key",
    },
    {
      value: "login",
      label: (
        <div className="text-[1vw] px-[0.2vw] pb-[0.1vw] font-normal">
          Login
        </div>
      ),
      search: "Login",
    },
    {
      value: "database",
      label: (
        <div className="text-[1vw] px-[0.2vw] pb-[0.1vw] font-normal">
          Database
        </div>
      ),
      search: "Database",
    },
    {
      value: "cloud",
      label: (
        <div className="text-[1vw] px-[0.2vw] pb-[0.1vw] font-normal">
          Cloud
        </div>
      ),
      search: "Cloud",
    },
    {
      value: "token",
      label: (
        <div className="text-[1vw] px-[0.2vw] pb-[0.1vw] font-normal">
          Token
        </div>
      ),
      search: "Token",
    },
  ];

  useEffect(() => {
    if (!credentialId || !isEdit) return;

    const data = window.__EDIT_CREDENTIAL_DATA__;

    if (data) {
      setInitialValues({
        credential_type: data?.credential_type || "",
        credential_name: data?.credential_name || "",
        provider_name: data?.provider_name || "",
        username: data?.username || "",
        secret: data?.secret || "",
        expires_at: data?.expires_at || "",
        notes: data?.notes || "",
        is_sensitive: data?.is_sensitive ?? false,
      });
    }
  }, [credentialId, isEdit]);

  const handleTextChange = (e, handleChange) => {
    const rawValue = e.target.value;
    const cleanedValue = rawValue.replace(/^\s+/, "").replace(/\s{2,}/g, " ");

    handleChange({
      target: {
        name: e.target.name,
        value: cleanedValue,
      },
    });
  };

  const buildSubmitPayload = async (values) => {
    return {
      ...values,
    };
  };

  const handleSubmit = async (values) => {
    if (isView) return;

    setLoading(true);

    try {
      const payload = await buildSubmitPayload(values);

      const response = await SubmitCredentialData(
        payload,
        isEdit ? credentialId : null,
      );

      if (response?.message?.toLowerCase()?.includes("otp")) {
        setPendingValues(values);
        setOtpModalOpen(true);
        return;
      }

      if (response) {
        onSuccess?.();
        setModalIsOpen(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUpdateOtp = async (plainOtp) => {
    if (!pendingValues || !credentialId) return;

    try {
      setOtpLoading(true);

      const encryptedOtp = await encryptVaultSecret(plainOtp);

      const response = await EditSensCredential(
        encryptedOtp,
        pendingValues,
        credentialId,
      );

      if (response) {
        setOtpModalOpen(false);
        setPendingValues(null);
        onSuccess?.();
        setModalIsOpen(false);
      }
    } finally {
      setOtpLoading(false);
    }
  };

  const handleCloseOtp = () => {
    setOtpModalOpen(false);
    setPendingValues(null);
  };

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 bg-white/60 z-20 flex items-center justify-center">
          <Spin size="large" />
        </div>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, setFieldValue }) => (
          <Form>
            <div className="flex justify-between mr-[1vw]">
              <div className="text-[1.4vw] text-[#1F4B7F] font-semibold">
                {isView
                  ? "VIEW CREDENTIAL"
                  : isEdit
                    ? "UPDATE CREDENTIAL"
                    : "ADD CREDENTIAL"}
              </div>

              <div className="flex gap-x-[1vw]">
                <button
                  type="button"
                  className="flex text-[#1F4B7F] bg-white px-[2vw] gap-[0.5vw] py-[0.5vw] rounded-[0.7vw] items-center justify-center border-[#1F4B7F] border-[0.1vw] text-[1.1vw] font-semibold"
                  onClick={() => setModalIsOpen(false)}
                >
                  Close
                </button>

                {!isView && (
                  <button
                    type="submit"
                    className="flex text-white bg-[#1F4B7F] px-[2vw] gap-[0.5vw] py-[0.5vw] rounded-[0.7vw] items-center justify-center text-[1.1vw] font-semibold"
                  >
                    Save
                  </button>
                )}
              </div>
            </div>

            <div className="umselect">
              <div className="grid grid-cols-2 gap-[1vw] pt-[1vw]">
                <div className="relative col-span-1 flex-col flex">
                  <label className="text-[#1F4B7F] text-[1.1vw] font-semibold">
                    Type
                    <span className="text-red-500 text-[1vw] pl-[0.2vw]">
                      *
                    </span>
                  </label>

                  <ConfigProvider
                    theme={{
                      components: {
                        Select: {
                          optionActiveBg: "#aebed1",
                          optionSelectedColor: "#1F487C",
                          optionSelectedBg: "#e5e5e5",
                          optionHeight: "2vw",
                        },
                      },
                    }}
                  >
                    <Select
                      showSearch
                      disabled={isView}
                      value={values.credential_type || undefined}
                      className="custom-select bg-white outline-none w-full mt-[0.5vw] h-[3vw] text-[1.1vw] border-[#1F4B7F] border-l-[0.1vw] border-t-[0.1vw] rounded-[0.5vw] border-r-[0.25vw] border-b-[0.25vw]"
                      placeholder="Select Type"
                      optionFilterProp="search"
                      listHeight={250}
                      dropdownStyle={{
                        overflowY: "auto",
                        scrollbarWidth: "0.1vw",
                      }}
                      onChange={(value) =>
                        setFieldValue("credential_type", value)
                      }
                      suffixIcon={
                        <span style={{ fontSize: "1vw", color: "#1f487c" }}>
                          <IoMdArrowDropdown size="2vw" />
                        </span>
                      }
                      style={{ padding: 4 }}
                      options={typeOptions}
                    />
                  </ConfigProvider>

                  <ErrorMessage
                    name="credential_type"
                    component="div"
                    className="text-red-500 text-[0.8vw] absolute bottom-[-1.2vw]"
                  />
                </div>

                <div className="relative col-span-1 flex-col flex">
                  <label className="text-[#1F4B7F] text-[1.1vw] font-semibold">
                    Name
                    <span className="text-red-500 text-[1vw] pl-[0.2vw]">
                      *
                    </span>
                  </label>

                  <Field
                    type="text"
                    name="credential_name"
                    placeholder="Enter Name"
                    value={values.credential_name}
                    disabled={isView}
                    autoComplete="off"
                    onChange={(e) => handleTextChange(e, handleChange)}
                    className="border-r-[0.25vw] mt-[0.5vw] border-l-[0.1vw] border-t-[0.1vw] border-b-[0.25vw] border-[#1F487C] text-[#1F487C] text-[1vw] h-[3vw] w-full rounded-[0.5vw] outline-none px-[1vw] placeholder:text-[1vw] placeholder:font-sans disabled:bg-white"
                  />

                  <ErrorMessage
                    name="credential_name"
                    component="div"
                    className="text-red-500 text-[0.8vw] absolute bottom-[-1.2vw]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-[1vw] pt-[1vw]">
                <div className="relative col-span-1 flex-col flex">
                  <label className="text-[#1F4B7F] text-[1.1vw] font-semibold">
                    Owner
                    <span className="text-red-500 text-[1vw] pl-[0.2vw]">
                      *
                    </span>
                  </label>

                  <Field
                    type="text"
                    name="provider_name"
                    placeholder="Enter Owner"
                    value={values.provider_name}
                    disabled={isView}
                    autoComplete="off"
                    onChange={(e) => handleTextChange(e, handleChange)}
                    className="border-r-[0.25vw] mt-[0.5vw] border-l-[0.1vw] border-t-[0.1vw] border-b-[0.25vw] border-[#1F487C] text-[#1F487C] text-[1vw] h-[3vw] w-full rounded-[0.5vw] outline-none px-[1vw] placeholder:text-[1vw] placeholder:font-sans disabled:bg-white"
                  />

                  <ErrorMessage
                    name="provider_name"
                    component="div"
                    className="text-red-500 text-[0.8vw] absolute bottom-[-1.2vw]"
                  />
                </div>

                <div className="relative col-span-1 flex-col flex">
                  <label className="text-[#1F4B7F] text-[1.1vw] font-semibold">
                    Username / Identifier
                    <span className="text-red-500 text-[1vw] pl-[0.2vw]">
                      *
                    </span>
                  </label>

                  <Field
                    type="text"
                    name="username"
                    placeholder="Enter Username / Identifier"
                    value={values.username}
                    disabled={isView}
                    autoComplete="off"
                    onChange={(e) => handleTextChange(e, handleChange)}
                    className="border-r-[0.25vw] mt-[0.5vw] border-l-[0.1vw] border-t-[0.1vw] border-b-[0.25vw] border-[#1F487C] text-[#1F487C] text-[1vw] h-[3vw] w-full rounded-[0.5vw] outline-none px-[1vw] placeholder:text-[1vw] placeholder:font-sans disabled:bg-white"
                  />

                  <ErrorMessage
                    name="username"
                    component="div"
                    className="text-red-500 text-[0.8vw] absolute bottom-[-1.2vw]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-[1vw] pt-[1vw]">
                <div className="relative col-span-1 flex-col flex">
                  <label className="text-[#1F4B7F] text-[1.1vw] font-semibold">
                    Secret
                    <span className="text-red-500 text-[1vw] pl-[0.2vw]">
                      *
                    </span>
                  </label>

                  <Field
                    type="text"
                    name="secret"
                    placeholder="Enter Secret"
                    value={values.secret}
                    disabled={isView}
                    autoComplete="off"
                    onChange={(e) => handleTextChange(e, handleChange)}
                    className="border-r-[0.25vw] mt-[0.5vw] border-l-[0.1vw] border-t-[0.1vw] border-b-[0.25vw] border-[#1F487C] text-[#1F487C] text-[1vw] h-[3vw] w-full rounded-[0.5vw] outline-none px-[1vw] placeholder:text-[1vw] placeholder:font-sans disabled:bg-white"
                  />

                  <ErrorMessage
                    name="secret"
                    component="div"
                    className="text-red-500 text-[0.8vw] absolute bottom-[-1.2vw]"
                  />
                </div>

                <div className="relative col-span-1 flex-col flex">
                  <label className="text-[#1F4B7F] text-[1.1vw] font-semibold">
                    Expires
                    <span className="text-red-500 text-[1vw] pl-[0.2vw]">
                      *
                    </span>
                  </label>

                  <ConfigProvider
                    theme={{
                      token: {
                        fontSize: ".9vw",
                        lineHeight: 0,
                        colorPrimary: "#1F487C",
                      },
                      components: {
                        DatePicker: {
                          activeBorderColor: "#1F487C",
                          hoverBorderColor: "#1F487C",
                          activeShadow: "#1F487C",
                          cellWidth: 25,
                          cellHeight: 20,
                        },
                      },
                    }}
                  >
                    <DatePicker
                      disabled={isView}
                      disabledDate={(current) =>
                        current && current < dayjs().startOf("day")
                      }
                      inputReadOnly={true}
                      value={
                        values.expires_at ? dayjs(values.expires_at) : null
                      }
                      format="DD MMM, YYYY"
                      placeholder="Select Expires Date"
                      onChange={(date) =>
                        setFieldValue(
                          "expires_at",
                          date ? date.toISOString() : "",
                        )
                      }
                      className="ads-date border-r-[0.25vw] mt-[0.5vw] border-l-[0.1vw] border-t-[0.1vw] border-b-[0.25vw] placeholder-blue border-[#1F487C] text-[#1F487C] text-[1vw] h-[3vw] w-full rounded-[0.5vw] outline-none px-[1vw] placeholder:text-[1vw] disabled:bg-white"
                    />
                  </ConfigProvider>

                  <ErrorMessage
                    name="expires_at"
                    component="div"
                    className="text-red-500 text-[0.8vw] absolute bottom-[-1.2vw]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-[1vw] pt-[1vw]">
                <div className="relative col-span-1 flex flex-col">
                  <label className="text-[#1F4B7F] text-[1.1vw] font-semibold">
                    Sensitive
                  </label>

                  <ConfigProvider
                    theme={{
                      components: {
                        Select: {
                          optionActiveBg: "#aebed1",
                          optionSelectedColor: "#1F487C",
                          optionSelectedBg: "#e5e5e5",
                          optionHeight: "2vw",
                        },
                      },
                    }}
                  >
                    <Select
                      value={values.is_sensitive}
                      disabled={isView}
                      className="custom-select bg-white outline-none w-full mt-[0.5vw] h-[3vw] text-[1.1vw] border-[#1F4B7F] border-l-[0.1vw] border-t-[0.1vw] rounded-[0.5vw] border-r-[0.25vw] border-b-[0.25vw]"
                      onChange={(value) => setFieldValue("is_sensitive", value)}
                      getPopupContainer={(trigger) => trigger.parentNode}
                      dropdownStyle={{ maxHeight: "8vw", overflowY: "auto" }}
                      suffixIcon={
                        <span style={{ fontSize: "1vw", color: "#1f487c" }}>
                          <IoMdArrowDropdown size="2vw" />
                        </span>
                      }
                      style={{ padding: 4 }}
                      options={[
                        {
                          value: false,
                          label: (
                            <div className="text-[1vw] px-[0.2vw] pb-[0.1vw] font-normal">
                              No
                            </div>
                          ),
                        },
                        {
                          value: true,
                          label: (
                            <div className="text-[1vw] px-[0.2vw] pb-[0.1vw] font-normal">
                              Yes
                            </div>
                          ),
                        },
                      ]}
                    />
                  </ConfigProvider>
                </div>

                <div className="relative col-span-1 flex-col flex">
                  <label className="text-[#1F4B7F] text-[1.1vw] font-semibold">
                    Notes
                  </label>

                  <Field
                    as="textarea"
                    name="notes"
                    placeholder="Enter Notes"
                    value={values.notes}
                    disabled={isView}
                    autoComplete="off"
                    onChange={(e) => handleTextChange(e, handleChange)}
                    className="border-r-[0.25vw] mt-[0.5vw] border-l-[0.1vw] border-t-[0.1vw] border-b-[0.25vw] border-[#1F487C] text-[#1F487C] text-[1vw] h-[5.5vw] w-full rounded-[0.5vw] outline-none px-[1vw] py-[0.6vw] resize-none placeholder:text-[1vw] placeholder:font-sans disabled:bg-white"
                  />

                  <ErrorMessage
                    name="notes"
                    component="div"
                    className="text-red-500 text-[0.8vw] absolute bottom-[-1.2vw]"
                  />
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <CredentialsOtpModal
        show={otpModalOpen}
        actionType="update"
        loading={otpLoading}
        onClose={handleCloseOtp}
        onVerify={handleVerifyUpdateOtp}
        onResend={() => {}}
      />
    </div>
  );
};

export default CredentialForm;
